import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { usePrefersReducedMotion } from "./motion";
import { FlowGlowDefs, FlowPath } from "./flow";

/*
 * "Network flows" diagram - ported from the product presentation. Who talks to
 * whom, over which ports: on-premise systems and cloud services with port-labeled
 * edges. Flow packets travel along each edge (dropped under reduced motion, see
 * ./flow). Themed via navy-band tokens.
 */

const VB = { w: 1040, h: 420 };
const NWD = 140;
const NHD = 42;

// on-prem nodes: [label, x, y]
const RZ: Array<[string, number, number]> = [
  ["Web-Frontend", 120, 120],
  ["App-Server", 330, 110],
  ["DB-Cluster", 560, 130],
  ["AD / DC", 130, 250],
  ["Fileserver", 360, 270],
  ["Mailserver", 580, 250],
];
// cloud nodes (center x, center y)
const CL: Array<[string, number, number]> = [
  ["M365", 890, 120],
  ["Entra ID", 890, 210],
  ["AWS", 890, 300],
];
// edges: [from, to, port] - `to` "c0".. references a cloud node
const EDGES: Array<[number, number | string, string]> = [
  [0, 1, ":443"],
  [1, 2, ":1433"],
  [0, 3, ":445"],
  [3, 4, ":389"],
  [1, 4, ":445"],
  [4, 5, ":443"],
  [2, 5, ":25"],
  [1, "c0", ":443"],
  [2, "c1", ":443"],
  [5, "c2", ":443"],
  [3, "c1", ":3389"],
];

const pos = (i: number | string) => {
  if (typeof i === "string") {
    const n = CL[Number(i.slice(1))];
    return { x: n[1], y: n[2] };
  }
  const n = RZ[i];
  return { x: n[1] + NWD / 2, y: n[2] + NHD / 2 };
};

export const AutonubilNetworkFlows: React.FC = () => {
  const { translate } = useAnketaI18n();
  const reduced = usePrefersReducedMotion();
  const tp = "anketa.autonubil.home.flows";
  const t = (k: string, d: string) => translate(`${tp}.${k}`, { defaultMessage: d } as any);

  return (
    <div
      style={{ width: "100%" }}
      role="img"
      aria-label={t(
        "aria",
        "opsanio maps network flows: which systems communicate over which ports, in what volume, on-premise and into the cloud.",
      )}
    >
      <svg
        viewBox={`0 0 ${VB.w} ${VB.h}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: "block", overflow: "visible" }}
      >
        <FlowGlowDefs id="flowsGlow" />
        {/* frames */}
        <rect x={20} y={40} width={700} height={360} rx={16} fill="none" stroke="var(--ak-on-navy-border)" strokeWidth={1.2} strokeDasharray="4 5" />
        <text x={40} y={66} fill="var(--ak-accent-2)" fontSize={10.5} fontWeight={700} letterSpacing="0.12em">
          {t("onprem", "IHR RECHENZENTRUM · ON-PREMISE")}
        </text>
        <rect x={760} y={40} width={260} height={360} rx={16} fill="none" stroke="var(--ak-on-navy-border)" strokeWidth={1.2} strokeDasharray="4 5" />
        <text x={780} y={66} fill="#c9bdfa" fontSize={10.5} fontWeight={700} letterSpacing="0.12em">
          {t("cloud", "CLOUD")}
        </text>

        {/* edges + port labels */}
        {EDGES.map((e, i) => {
          const a = pos(e[0]);
          const b = pos(e[1]);
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          const d = `M${a.x},${a.y} C${mx},${a.y} ${mx},${b.y} ${b.x},${b.y}`;
          return (
            <g key={i}>
              <FlowPath id={`nf-${i}`} glowId="flowsGlow" reduced={reduced} dur={2.8 + (i % 4) * 0.35} d={d} />
              <rect x={mx - 17} y={my - 9} width={34} height={18} rx={5} fill="var(--ak-navy)" stroke="var(--ak-on-navy-border)" strokeWidth={0.8} />
              <text x={mx} y={my + 4} textAnchor="middle" fill="var(--ak-on-navy-secondary)" fontSize={10} fontWeight={600}>
                {e[2]}
              </text>
            </g>
          );
        })}

        {/* on-prem nodes */}
        {RZ.map((n) => (
          <g key={n[0]}>
            <rect x={n[1]} y={n[2]} width={NWD} height={NHD} rx={10} fill="var(--ak-navy-2)" stroke="var(--ak-accent-border)" strokeWidth={1.2} />
            <text x={n[1] + NWD / 2} y={n[2] + NHD / 2 + 4} textAnchor="middle" fill="var(--ak-on-navy)" fontWeight={600} fontSize={12}>
              {n[0]}
            </text>
          </g>
        ))}
        {/* cloud nodes */}
        {CL.map((n) => (
          <g key={n[0]}>
            <rect x={n[1] - 70} y={n[2] - 21} width={140} height={42} rx={10} fill="var(--ak-navy-2)" stroke="rgba(179,164,245,0.4)" strokeWidth={1.2} />
            <text x={n[1]} y={n[2] + 4} textAnchor="middle" fill="var(--ak-on-navy)" fontWeight={600} fontSize={12}>
              {n[0]}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default AutonubilNetworkFlows;
